import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Store as StoreIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  ExitToApp as LogoutIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const statusColors = {
  active: 'success',
  pending: 'warning',
  provisioning: 'info',
  suspended: 'error',
  completed: 'success',
  rejected: 'error'
};

function PlatformDashboard() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [applications, setApplications] = useState([]);

  const admin = JSON.parse(localStorage.getItem('platform_admin') || '{}');
  const token = localStorage.getItem('platform_token');

  useEffect(() => {
    if (!token) {
      navigate('/platform');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, tenantsRes, applicationsRes] = await Promise.all([
        api.get('/platform/dashboard', { headers }),
        api.get('/platform/tenants?limit=5', { headers }),
        api.get('/platform/applications?limit=5', { headers })
      ]);

      setDashboard(dashboardRes.data.data);
      setTenants(tenantsRes.data.data.items);
      setApplications(applicationsRes.data.data.items);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('platform_token');
        navigate('/platform');
      } else {
        setError(t('platform.dashboard.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_admin');
    navigate('/platform');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* AppBar */}
      <AppBar position="static">
        <Toolbar>
          <StoreIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t('platform.nav.title')}
          </Typography>
          <Button color="inherit" component={Link} to="/platform/tenants" startIcon={<PeopleIcon />}>
            {t('platform.nav.tenants')}
          </Button>
          <Button color="inherit" component={Link} to="/platform/applications" startIcon={<AssignmentIcon />}>
            {t('platform.nav.applications')}
          </Button>
          <Button color="inherit" component={Link} to="/platform/activations" startIcon={<AssignmentIcon />}>
            開通碼
          </Button>
          <Typography sx={{ mx: 2 }}>
            {admin.name || admin.username}
          </Typography>
          <IconButton color="inherit" onClick={fetchData}>
            <RefreshIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StoreIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">{t('platform.dashboard.totalTenants')}</Typography>
                    <Typography variant="h4">{dashboard?.tenants?.total || 0}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'success.light', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                    <Typography variant="body1" color="success.dark">{dashboard?.tenants?.active || 0}</Typography>
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2">{t('platform.dashboard.activeTenants')}</Typography>
                    <Typography variant="h4" color="success.main">{dashboard?.tenants?.active || 0}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssignmentIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">{t('platform.dashboard.pendingApplications')}</Typography>
                    <Typography variant="h4" color="warning.main">{dashboard?.applications?.pending || 0}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">{t('platform.dashboard.completedApplications')}</Typography>
                    <Typography variant="h4" color="info.main">{dashboard?.applications?.completed || 0}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Latest Tenants */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('platform.dashboard.latestTenants')}</Typography>
                <Button component={Link} to="/platform/tenants" size="small">{t('platform.dashboard.viewAll')}</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('platform.tenants.name')}</TableCell>
                      <TableCell>{t('platform.tenants.subdomain')}</TableCell>
                      <TableCell>{t('platform.tenants.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenants.map(tenant => (
                      <TableRow key={tenant.id}>
                        <TableCell>{tenant.name}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary">
                            {tenant.slug}.urstore.tw
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`platform.status.${tenant.status}`, { defaultValue: tenant.status })}
                            color={statusColors[tenant.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {tenants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">{t('platform.common.noData')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Latest Applications */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('platform.dashboard.latestApplications')}</Typography>
                <Button component={Link} to="/platform/applications" size="small">{t('platform.dashboard.viewAll')}</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('platform.applications.storeName')}</TableCell>
                      <TableCell>{t('platform.applications.applicant')}</TableCell>
                      <TableCell>{t('platform.tenants.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applications.map(app => (
                      <TableRow key={app.id}>
                        <TableCell>{app.platform_name}</TableCell>
                        <TableCell>{app.applicant_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`platform.status.${app.status}`, { defaultValue: app.status })}
                            color={statusColors[app.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {applications.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">{t('platform.common.noData')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default PlatformDashboard;
